import React from 'react';
import {
  Box,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  Paper,
  Alert,
} from '@mui/material';
import { FlowComponent } from '../../../../flow-core/FlowComponent';
import { RichTextCore } from '../../../../flow-core/components/RichTextCore';

// Simple markdown preview renderer
var renderMarkdownPreview = (text) => {
  if (!text) return null;

  var lines = Array.isArray(text) ? text : [text];
  var combinedText = lines.join('\n');

  // Convert markdown to simple styled components for preview
  return combinedText.split('\n').map((line, index) => {
    // H1: # Header
    if (line.startsWith('# ')) {
      return (
        <Typography key={index} variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
          {line.substring(2)}
        </Typography>
      );
    }
    // H2: ## Header
    if (line.startsWith('## ')) {
      return (
        <Typography key={index} variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
          {line.substring(3)}
        </Typography>
      );
    }
    // Bullet list
    if (line.startsWith('- ') || line.startsWith('* ')) {
      return (
        <Typography key={index} variant="body2" sx={{ ml: 2 }}>
          {'\u2022 '}{line.substring(2)}
        </Typography>
      );
    }
    // Numbered list
    var numberedMatch = line.match(/^(\d+)\.\s(.+)/);
    if (numberedMatch) {
      return (
        <Typography key={index} variant="body2" sx={{ ml: 2 }}>
          {numberedMatch[1]}. {numberedMatch[2]}
        </Typography>
      );
    }
    // Empty line
    if (line.trim() === '') {
      return <Box key={index} sx={{ height: '0.5em' }} />;
    }
    // Regular text with inline formatting
    var formattedLine = line
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/~~(.+?)~~/g, '<del>$1</del>')
      .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2">$1</a>');

    return (
      <Typography
        key={index}
        variant="body2"
        component="div"
        dangerouslySetInnerHTML={{ __html: formattedLine }}
        sx={{ '& a': { color: 'primary.main' } }}
      />
    );
  });
};

export const RichTextComponent = new FlowComponent({
  ...RichTextCore,
  renderPreview(properties) {
    return (
      <Box sx={{ opacity: properties.visible === false ? 0.5 : 1 }}>
        {/* WhatsApp draws rich text straight onto the screen, with no box round it. */}
        {renderMarkdownPreview(properties.text)}
      </Box>
    );
  },
  renderPropertyEditor(properties, onChange) {
    var textValue = Array.isArray(properties.text)
      ? properties.text.join('\n')
      : (properties.text || '');

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Alert severity="info" sx={{ mb: 1 }}>
          RichText supports Markdown: # H1, ## H2, **bold**, *italic*, ~~strikethrough~~,
          - bullets, 1. numbered lists, and [links](url).
        </Alert>

        <TextField
          fullWidth
          label="Markdown Content"
          value={textValue}
          onChange={(e) => onChange('text', e.target.value)}
          required
          multiline
          rows={10}
          size="small"
          helperText={`${textValue.length}/4096 characters`}
          inputProps={{ maxLength: 4096 }}
        />

        <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }}>
          Preview
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
          {renderMarkdownPreview(textValue)}
        </Paper>

        <FormControlLabel
          control={
            <Switch
              checked={properties.visible !== false}
              onChange={(e) => onChange('visible', e.target.checked)}
            />
          }
          label="Visible"
        />
      </Box>
    );
  },
});
